import React, { forwardRef } from "react";
import { ReactComponent as FincaIcon } from '../assets/icons/FincaIcon.svg';
import { ReactComponent as QR } from '../assets/icons/QR.svg';

const obtenerFechaActual = () => {
    const hoy = new Date();
    const dia = String(hoy.getDate()).padStart(2, '0');
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const anio = hoy.getFullYear();
    return `${dia}/${mes}/${anio}`;
};

const formatearFechaVenta = (fecha) => {
    if (!fecha) return '';
    try {
        const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
        if (isNaN(d.getTime())) return '';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const anio = d.getFullYear();
        return `${dia}/${mes}/${anio}`;
    } catch {
        return '';
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
};

const SIMBOLOS_DIVISA = { Pesos: "$", "Dólares": "US$", Reales: "R$", Euros: "€" };

const Factura = forwardRef(({ 
    datosCliente, 
    datosCheque, 
    productosVendidos, 
    tipoFactura, 
    totalNeto, 
    iva21, 
    iva10_5, 
    totalFinal, 
    numeroComprobante, 
    modalidadPago, 
    tipoDivisa = 'Pesos', 
    copia = 'ORIGINAL',
    fechaVenta 
}, ref) => {
    const simboloDivisa = SIMBOLOS_DIVISA[tipoDivisa] || "$";
    
    const fechaEmisionCalculada = formatearFechaVenta(fechaVenta) || obtenerFechaActual();

    const datosVendedor = {
        razonSocial: "Finca Los Astros S.A.S",
        domicilio: "Servidumbre de Tránsito s/n Fracción C 0, Piso C, El Peral, 5561, Provincia de Mendoza, Argentina",
        telefono: "+5491130609103",
        condicionIVA: "Responsable Inscripto",
        cuit: "30-71934761-0",
        ingresosBrutos: "1038057",
        fechaInicioActividades: "22/12/2025",
        puntoDeVenta: "00001",
        compNro: "00000001",
        fechaEmision: fechaEmisionCalculada
    };

    const tipoComprobante = tipoFactura === 'A' ? 'A' : tipoFactura === 'B' ? 'B' : tipoFactura === 'E' ? 'E' : tipoFactura === 'R' ? 'R' : 'X';
    const codComprobante = tipoFactura === 'A' ? 'COD.01' : tipoFactura === 'B' ? 'COD.06' : tipoFactura === 'E' ? 'COD.19' : tipoFactura === 'R' ? 'COD.91' : 'COD.00';
    
    const getCondicionIVA = () => 'Consumidor final';

    const renderModalidadPago = () => {
        if (modalidadPago === 'Cheque' && datosCheque) {
            const { numeroCheque, bancoEmisor, fechaVencimiento } = datosCheque;
            const fechaFormateada = formatDate(fechaVencimiento);
            return (
                <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                    <b>Condición de venta</b><br />
                    {modalidadPago}<br />
                    {numeroCheque}<br />
                    {bancoEmisor}<br />
                    Fecha de vencimiento: {fechaFormateada}<br />
                </p>
            );
        }
        return (
            <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                <b>Condición de venta</b><br />
                {modalidadPago}
            </p>
        );
    };

    const formatNumber = (number) => {
        return number?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const renderProductos = () => {
        const getUnidadAbreviada = (unidad) => {
            switch (unidad) {
                case 'Hectolitros': return 'hl';
                case 'Quintales': return 'q';
                default: return 'u';
            }
        };
        
        const isSeñaRecibo = tipoFactura === 'R';
        const totalAUsarParaSeña = isSeñaRecibo ? totalFinal : null;
        
        return productosVendidos.map((item, index) => {
            const descuentoReal = Number(item?.descuento) || 0;
            const factorDescuento = (100 - descuentoReal) / 100;
            
            const precioBaseTotalBruto = item?.cantidad * item?.precioUnidad;
            const precioBase = precioBaseTotalBruto * factorDescuento; 
            
            const ivaPorProducto = item?.nombreProducto?.startsWith('VIT') ? 0.105 : 0.21;
            const totalProducto100 = precioBase * (1 + ivaPorProducto);
            
            const porcentajeDescuento = isSeñaRecibo ? '50%' : `${descuentoReal}%`;
            
            let totalRenderizado;
            if (isSeñaRecibo) {
                totalRenderizado = index === 0 ? totalAUsarParaSeña : 0;
            } else {
                totalRenderizado = tipoFactura === 'A' ? totalProducto100 : precioBase;
            }
            
            const unidadAbreviada = getUnidadAbreviada(item?.tipoUnidad);
            
            return (
                <tr key={index}>
                    <td style={{ textAlign: 'left', maxWidth: '110px' }}>{item?.nombreProducto}</td>
                    <td style={tdStyle}>{item?.cantidad}</td>
                    <td style={tdStyle}>{unidadAbreviada}</td>
                    <td style={tdStyleRight}>{simboloDivisa}{formatNumber(item?.precioUnidad)}</td>
                    <td style={tdStyle}>{porcentajeDescuento}</td>
                    {tipoFactura === 'A' && (
                        <td style={tdStyle}>{ivaPorProducto === 0.105 ? '10.5%' : '21%'}</td>
                    )}
                    <td style={tdStyleRight}>{simboloDivisa}{formatNumber(totalRenderizado)}</td>
                </tr>
            );
        });
    };

    const esPersonaFisica = 
        datosCliente?.personeria?.toLowerCase().includes('fisica') || 
        datosCliente?.personeria?.toLowerCase().includes('física') ||
        Boolean(datosCliente?.nombre || datosCliente?.apellido || datosCliente?.numeroDeDocumento);

    const nombreOrazonSocial = esPersonaFisica
        ? `${datosCliente?.apellido || ''}, ${datosCliente?.nombre || ''}`.trim().replace(/^,|,$/g, '')
        : (datosCliente?.razonSocial || '');

    const documentoOCuit = esPersonaFisica
        ? (datosCliente?.numeroDeDocumento || '')
        : (datosCliente?.cuit || datosCliente?.numeroDeDocumento || '');

    const tipoDoc = esPersonaFisica 
        ? (datosCliente?.tipo || 'DNI') 
        : 'CUIL / CUIT';

    return (
        <div
            ref={ref}
            style={{
                minWidth: "210mm",
                minHeight: "297mm",
                padding: "17mm 10mm 10mm",
                background: "#fff",
                fontFamily: "Arial, sans-serif",
                color: "#333",
                margin: "0 auto",
                border: "1px solid #ddd",
                borderRadius: "4px",
                display: 'flex',
                flexDirection: 'column',
                boxSizing: "border-box",
                position: 'relative',
            }}
        >
            <div style={{ display: "flex", alignItems: "center" }}>
                <FincaIcon style={{ width: "32%", overflow: 'visible' }} />
            </div>
            <div style={{ position: 'absolute', top: '.75cm', right: '46%', textAlign: 'center', alignItems: 'center', justifyContent: 'center', gap: '18px', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: 'Courier New', fontWeight: 'normal', textAlign: 'center', margin: ' auto' }}>{copia}</span>
                <div style={{ backgroundColor: 'white', border: '1px solid #808080', justifyContent: 'space-between', alignContent: 'center', width: '1.75cm', height: '1.75cm', padding: '5px', borderRadius: '10px', display: 'flex', flexDirection: 'column' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontWeight: 'normal', textAlign: 'center', fontSize: '35px', margin: 'auto' }}>{tipoComprobante}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontWeight: 'normal', textAlign: 'center', fontSize: '12px', margin: 'auto' }}>{codComprobante}</p>
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: '50px', width: '100%' }}>
                <div style={{ width: 'fit-content' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Razón social</b><br />{datosVendedor.razonSocial}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                        <b>Domicilio Comercial</b><br />
                        {datosVendedor.domicilio.split(', ').map((line, i) =>
                            <span key={i} style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>{line}<br /></span>
                        )}
                    </p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Teléfono de contacto</b><br />{datosVendedor.telefono}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Condición frente al IVA</b><br />{datosVendedor.condicionIVA}</p>
                </div>
                <div style={{ width: 'fit-content' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                        <b>{tipoFactura === 'R' ? 'REMITO' : 'FACTURA'}</b></p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Punto de venta: {datosVendedor.puntoDeVenta}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Comp.Nro.: {numeroComprobante || datosVendedor.compNro}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Fecha de emisión: {datosVendedor.fechaEmision}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>CUIT: {datosVendedor.cuit}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Ingresos Brutos: {datosVendedor.ingresosBrutos}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Fecha de Inicio de Actividades: {datosVendedor.fechaInicioActividades}</p>
                </div>
            </div>
            <div style={{ borderTop: '1px solid #808080', borderBottom: '1px solid #808080', opacity: '0.5', height: '1mm', margin: '10px 0' }}></div>
            {datosCliente && (
                <div style={{ display: "flex", justifyContent: "space-between", width: '100%' }}>
                    <div style={{ width: 'fit-content' }}>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                            <b>{tipoDoc}</b><br />{documentoOCuit}
                        </p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                            <b>Condición frente al IVA</b><br />{getCondicionIVA()}
                        </p>
                        {renderModalidadPago()}
                    </div>
                    <div style={{ width: 'fit-content' }}>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                            <b>Apellido y Nombre / Razón Social</b><br />{nombreOrazonSocial}
                        </p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                            <b>Domicilio</b><br />
                            {datosCliente?.calle} {datosCliente?.altura}, {datosCliente?.piso} {datosCliente?.unidad}<br />
                            {datosCliente?.codigoPostal}<br />
                            {datosCliente?.ciudad}, {datosCliente?.pais}
                        </p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                            <b>Teléfono de contacto</b><br />{datosCliente?.telefono}
                        </p>
                    </div>
                </div>
            )}
            <div style={{ borderTop: '1px solid #808080', borderBottom: '1px solid #808080', opacity: '0.5', height: '1mm', margin: '10px 0' }}></div>
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontFamily: "'Courier New'",
                    fontSize: "11px",
                }}
            >
                <thead>
                    <tr>
                        <th style={thStyle}>Descripción</th>
                        <th style={thStyle}>Cantidad</th>
                        <th style={thStyle}>Unidad</th>
                        <th style={{ ...thStyle, minWidth: '75px' }}>Precio Uni.</th>
                        <th style={thStyle}>%DESC</th>
                        {tipoFactura === 'A' && (
                            <th style={thStyle}>%IVA</th>
                        )}
                        <th style={thStyle}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {renderProductos()}
                </tbody>
            </table>
            <div style={{
                margin: 'auto auto 0',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                justifyContent: 'space-between',
                fontFamily: "'Courier New'",
                gap: '10px'
            }}>
                <div style={{
                    borderTop: '1px solid #cccccc',
                    padding: '10px 0',
                    borderBottom: '1px solid #cccccc', 
                    display: 'flex', 
                    flexDirection: 'row', 
                    justifyContent: 'space-between', 
                    width: '100%'
                }}>
                    {tipoFactura === 'A' ? (
                        <>
                            <table style={{ borderCollapse: 'collapse', width: '40%', fontSize: '11px', flexGrow: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...thStyle, fontWeight: 'bold' }}>Descripción</th>
                                        <th style={{ ...thStyle, fontWeight: 'bold' }}>Importe</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0' }}>Importe neto gravado</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalNeto)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0' }}>IVA 21.00%</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(iva21)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0' }}>IVA 10.50%</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(iva10_5)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ fontSize: '14px', height: '100%', margin: '0 0 auto auto' }}>
                                <table style={{ borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Subtotal</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalFinal)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Importe Otros Tributos</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>0.00</td>
                                        </tr>
                                        <tr>
                                            <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Importe total </td>
                                            <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalFinal)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div style={{ fontSize: '14px', height: '100%', margin: '0 0 auto auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: '14px' }}>
                                <tbody>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Subtotal</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalFinal)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Importe Otros Tributos</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>0.00</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Importe total </td>
                                        <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalFinal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                    <QR style={{ height: '1.5cm', overflow: 'visible' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '10px', fontWeight: 'bold' }}>CAE Nº:12345678912345</p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '10px', fontWeight: 'bold' }}>Fecha de vencimiento de CAE:05/11/2028</p>
                    </div>
                </div>
            </div>
        </div>
    );
});

const thStyle = {
    border: "none",
    padding: "4px",
    background: "none",
    textAlign: "center",
    textTransform: 'uppercase',
    minWidth: '50px',
    flexWrap: 'nowrap'
};
const tdStyle = {
    border: "none",
    padding: "4px",
    textAlign: "center",
    minWidth: '50px',
};
const tdStyleRight = {
    border: "none",
    padding: "4px",
    paddingRight: '10px',
    textAlign: "right",
    minWidth: '50px',
};
export default Factura;